import { buildSeedDb, migrateDb, SCHEMA_VERSION, type Db } from "@love-cpmd/core";

/**
 * Mock database persisted in localStorage.
 * This is the only file that knows where data lives; replace it (and `./index.ts`)
 * with Supabase calls once the backend plan is agreed.
 */
const KEY = "love-cpmd:db";
let cache: Db | null = null;
const listeners = new Set<() => void>();

function read(): Db {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Db;
      if (parsed?.schemaVersion === SCHEMA_VERSION) return parsed;
      const upgraded = parsed ? migrateDb(parsed) : null;
      if (upgraded) {
        write(upgraded);
        return upgraded;
      }
    }
  } catch {
    /* corrupted or unavailable storage → reseed */
  }
  const fresh = buildSeedDb(new Date());
  write(fresh);
  return fresh;
}

function write(db: Db) {
  try {
    localStorage.setItem(KEY, JSON.stringify(db));
  } catch (error) {
    console.warn("[love-cpmd] Could not persist data", error);
  }
}

export function getDb(): Db {
  if (!cache) cache = read();
  return cache;
}

/** Copy-on-write mutation: the callback edits a draft; the draft replaces the store when it returns. */
export function mutateDb<T>(fn: (draft: Db) => T): T {
  const draft = structuredClone(getDb());
  const result = fn(draft);
  cache = draft;
  write(draft);
  listeners.forEach((l) => l());
  return result;
}

export function replaceDb(db: Db) {
  cache = db;
  write(db);
  listeners.forEach((l) => l());
}

export function subscribeDb(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Another tab changed the data → drop the cache and notify. */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === KEY) {
      cache = null;
      listeners.forEach((l) => l());
    }
  });
}
